import React, { useState, useEffect } from 'react';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { IconButton } from '@mui/material';
import Badge from '@mui/material/Badge';
import { Link, useNavigate } from 'react-router-dom';
import Logout from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

import Toggle from './Toggle';
import { useAppSelector, useAppDispatch } from '../../store/hooks/useTypedSelector';
import { RootState } from '../../store/store';
import { logoutUser } from '../../store/slices/loginSlice';
import { updateCartOnPageReload } from '../../store/slices/cartSlice';
import SearchContainer from '../SearchContainer';
import { userDetailsResponse } from '../../types';
import { COLORS } from '../../utils/ColorsTheme';
import './_header.scss';

const Header: React.FC = () => {
  const totalQuantity = useAppSelector((state: RootState) => state.cart.totalQuantity);
  const userDetailsReducerData = useAppSelector((state: RootState) => state.login.userDetails);
  let userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const navigate = useNavigate();
  const isDarkMode = theme.palette.mode === 'dark';
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    const userDetailsForLogoutUser: userDetailsResponse = {
      name: '',
      picture: '',
      email: '',
    };
    dispatch(logoutUser(userDetailsForLogoutUser));
    localStorage.setItem('userDetails', JSON.stringify(userDetailsForLogoutUser));
    localStorage.setItem('isLogin', 'false');
    userDetails = userDetailsForLogoutUser;
    setAnchorEl(null);
    navigate('/login', { replace: false });
  };
  const [isImageError, setImageError] = useState<boolean>(false);
  if (!userDetails.name) {
    userDetails = userDetailsReducerData;
  }
  useEffect(() => {
    if (totalQuantity === 0) {
      const cartDetails = JSON.parse(localStorage.getItem('cartDetails') || '[]');
      if (cartDetails.length) {
        dispatch(updateCartOnPageReload(cartDetails));
      }
    }
  }, []);

  return (
    <>
      <header className='header sticky-top'>
        <section className='header-main'>
          <div className='container-fluid'>
            <div
              className='row p-2 pt-3 pb-3 d-flex align-items-center'
              style={{
                backgroundColor: isDarkMode ? COLORS.secondaryDarkMode : COLORS.headerLightMode,
              }}
            >
              <div className='col-md-3'>
                <Link
                  to='/'
                  style={{ textDecoration: 'none' }}
                  className='d-flex justify-content-center align-items-center'
                >
                  <img
                    className='d-flex'
                    src='https://e-commerce-store-backend-ten.vercel.app/logo.png'
                    width='60'
                  />
                  <span
                    className={`header__logo fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`}
                  >
                    Akshay Bandivadekar&apos;s Store
                  </span>
                </Link>
              </div>
              <SearchContainer />
              <div className='col-md-2'>
                <div className='d-flex flex-row align-items-center justify-content-center'>
                  {!userDetails.name ? (
                    <Link
                      to='/login'
                      style={{ textDecoration: 'none', width: '164px', marginLeft: '-40px' }}
                    >
                      <div className='d-flex'>
                        <span className={`fw-bold ${isDarkMode ? 'text-white' : 'text-dark'} fs-6`}>
                          SignIn /
                        </span>
                        <span
                          className={`fw-bold ${isDarkMode ? 'text-white' : 'text-dark'} fs-6`}
                          style={{ marginLeft: '4px' }}
                        >
                          SignUp
                        </span>
                      </div>
                    </Link>
                  ) : (
                    <>
                      <IconButton
                        onClick={handleClick}
                        size='small'
                        aria-controls={open ? 'account-menu' : undefined}
                        aria-haspopup='true'
                        aria-expanded={open ? 'true' : undefined}
                        data-testid='logout'
                      >
                        <div className='d-flex align-items-center header__avatar_container'>
                          {!isImageError && userDetails.picture ? (
                            <img
                              src={userDetails.picture}
                              style={{ width: 32, borderRadius: 50, height: 32 }}
                              onError={() => setImageError(true)}
                            />
                          ) : (
                            <Avatar
                              sx={{ width: 32, height: 32 }}
                              className={isDarkMode ? 'text-white' : 'text-dark'}
                            >
                              {userDetails.name.charAt(0)}
                            </Avatar>
                          )}
                          <Tooltip title={userDetails.name}>
                            <span
                              className={`ms-2 fw-bold fs-6 avatar-name ${
                                isDarkMode ? 'text-white' : 'text-dark'
                              }`}
                            >
                              {userDetails.name}
                            </span>
                          </Tooltip>
                        </div>
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        id='account-menu'
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                        PaperProps={{
                          elevation: 0,
                          sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                              width: 32,
                              height: 32,
                              ml: -0.5,
                              mr: 1,
                            },
                            '&:before': {
                              content: '""',
                              display: 'block',
                              position: 'absolute',
                              top: 0,
                              right: 14,
                              width: 10,
                              height: 10,
                              bgcolor: 'background.paper',
                              transform: 'translateY(-50%) rotate(45deg)',
                              zIndex: 0,
                            },
                          },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      >
                        <MenuItem onClick={handleLogout} data-testid='logout-button'>
                          <ListItemIcon>
                            <Logout fontSize='small' />
                          </ListItemIcon>
                          Logout
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                  <div className='d-flex align-items-center ms-4 text-white'>
                    <Toggle />
                    <Link to='/cart' style={{ textDecoration: 'none' }}>
                      <Badge badgeContent={totalQuantity} color='primary'>
                        <ShoppingCartIcon
                          className='ms-2'
                          htmlColor={isDarkMode ? COLORS.darkModeColor : COLORS.lightModeColor}
                        />
                      </Badge>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </header>
    </>
  );
};

export default Header;
